/**
 * src/shell/components/AppErrorBoundary.tsx
 *
 * Universal Top-Level React Error Boundary.
 * Catches unhandled exceptions across module slots, dynamic i18n, and views.
 * Provides:
 * 1. Crash isolation — keeps the shell header and navigation functional.
 * 2. 1-click component reload.
 * 3. Reset to safe defaults.
 * 4. Diagnostic log export for bug reporting.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[AppErrorBoundary] Uncaught component crash:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) this.props.onReset();
  };

  private handleExportDiagnostics = () => {
    const report = {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      errorMessage: this.state.error?.message || 'Unknown error',
      errorStack: this.state.error?.stack || '',
      componentStack: this.state.errorInfo?.componentStack || '',
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crash-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          data-testid="app-error-boundary-screen"
          style={{
            padding: '32px',
            background: 'rgba(239, 68, 68, 0.05)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: '16px',
            margin: '16px',
            color: '#f8fafc',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div style={{ fontSize: '36px' }}>🛡️</div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#fca5a5' }}>
            {this.props.fallbackTitle || 'Component Recovered from Crash'}
          </h2>
          <p style={{ fontSize: '13px', color: '#cbd5e1', maxWidth: '480px', lineHeight: 1.5 }}>
            {this.props.fallbackMessage ||
              'A runtime exception was isolated by Sovereign Mesh Error Boundary to prevent shell collapse.'}
          </p>

          {this.state.error && (
            <pre
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '11px',
                color: '#f87171',
                maxWidth: '600px',
                overflow: 'auto',
                textAlign: 'left',
              }}
            >
              {this.state.error.message}
            </pre>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button
              type="button"
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#3b82f6',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Reload Component
            </button>
            <button
              type="button"
              data-testid="error-boundary-export-btn"
              onClick={this.handleExportDiagnostics}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#cbd5e1',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Export Diagnostic Report
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
